import React, { useState, useEffect, useRef } from 'react'
import Axios from 'axios'
import { Link } from "react-router-dom"
import BorderedLinesCell from './Column_Border';
import './Temporary_Inventory.css';
import { Host } from '../../../App';
import RigidSchedulePage from './Rigid_Flex_Schedule/Rigid_Table';
import FlexSchedulePage from './Rigid_Flex_Schedule/Flex_Table';

import Insert_Wood_Req from './Rigid_Flex_Schedule/Insert';
import Insert_Metal_Req from './Rigid_Flex_Schedule/Metal_Table';
import Slap_foam_Schedule_Page from './Rigid_Flex_Schedule/Slab_foam';

function Temp_Inventory() {
  const [postList, setPostList] = useState([]);
  const [Postartdate, setPoStartdate] = useState();
  const [Poenddate, setPoenddate] = useState('2026-12-31');
  const [customer, setCustomer] = useState({});
  const [Partdesc, setPartdesc] = useState({});
  const token = (window.localStorage.getItem("acessToken"));

  // ---- loading overlay ----
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(null);

  // ---- part type filters ----
  const [flexChecked, setFlexChecked] = useState(true);
  const [rigidChecked, setRigidChecked] = useState(true);
  const [woodenChecked, setWoodenChecked] = useState(true);
  const [metalChecked, setMetalChecked] = useState(true);
  const [slapfoamChecked, setSlapfoamChecked] = useState(true);

  const startLoading = () => {
    setLoading(true);
    setElapsed(0);
    if (elapsedRef.current) clearInterval(elapsedRef.current);
    const t0 = Date.now();
    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - t0) / 1000));
    }, 250);
  };
  const stopLoading = () => {
    if (elapsedRef.current) clearInterval(elapsedRef.current);
    elapsedRef.current = null;
    setLoading(false);
  };

  const printRAreaRef = useRef();
  const printFAreaRef = useRef();

  const queryParams = {
    enddate: Poenddate,
    startdate: Postartdate,
    Customer: customer,
    Part_Desc: Partdesc,
    token: token,
  };

  // Helper function to check if a line matches the selected part types
  const lineMatchesSelectedTypes = (line) => {
    if (!line || line.trim() === '') return false;

    const trimmedLine = line.trim();
    const hasFlex = flexChecked && (trimmedLine.includes('-F') || trimmedLine.match(/-F[:\s]/));
    const hasRigid = rigidChecked && (trimmedLine.includes('-R') || trimmedLine.match(/-R[:\s]/));
    const hasWooden = woodenChecked && (trimmedLine.includes('-W') || trimmedLine.match(/-W[:\s]/));
    const hasMetal =
      metalChecked &&
      /-M\s*(\(\-?\d+\))?\s*$/.test(trimmedLine);

    const hasSlapFoam =
      slapfoamChecked &&
      /-S(\s|$|\()/.test(trimmedLine);


    return hasFlex || hasRigid || hasWooden || hasMetal || hasSlapFoam;
  };

  // Filter list to show ONLY lines that match selected part types
  const filterListBySelectedTypes = (listText) => {
    if (!listText || listText.trim() === '') return '';

    const lines = listText.split('\n');
    const filteredLines = lines.filter(line => {
      const trimmedLine = line.trim();
      if (trimmedLine === '') return false; // Don't keep empty lines

      return lineMatchesSelectedTypes(trimmedLine);
    });

    if (filteredLines.length === 0) {
      return '';
    }

    return filteredLines.join('\n');
  };

  // Check if a row has any data for selected part types
  const rowHasDataForSelectedTypes = (row) => {
    // If all checkboxes are unchecked, show nothing
    if (!flexChecked && !rigidChecked && !woodenChecked && !metalChecked && !slapfoamChecked) {
      return false;
    }

    // Check all list columns to see if any contain data for selected types
    const listsToCheck = [
      row.Inventory_QTY_List,
      row.Produced_Parts_List,
      row.Scrap_Parts_List,
      row.Pending_Parts_List,
      row.PO_QTY_List,
      row.Required_Ship_QTY_List
    ];

    // Return true if ANY of the lists has data for the selected types
    return listsToCheck.some(list => {
      if (!list || list.trim() === '') return false;

      const lines = list.split('\n');
      return lines.some(line => lineMatchesSelectedTypes(line.trim()));
    });
  };

  // Filter PO Details as well (since PO_Details may also contain part type references)
  const filterPODetailsBySelectedTypes = (poDetails) => {
    if (!poDetails || poDetails.trim() === '') return poDetails || '';

    const lines = poDetails.split('\n');
    const filteredLines = lines.filter(line => {
      const trimmedLine = line.trim();
      if (trimmedLine === '') return false;

      return lineMatchesSelectedTypes(trimmedLine);
    });

    if (filteredLines.length === 0) {
      return '';
    }

    return filteredLines.join('\n');
  };

  /* ---------- toggle and filtered-rows -------------------------------- */
  const [showPOonly, setShowPOonly] = useState(true);

  // Get rows filtered by PO-only first
  const poFilteredRows = showPOonly
    ? postList.filter(v => (v.PO_Details || '').trim().length > 0)
    : postList;

  // Then filter by part types (row level filtering)
  const filteredRows = poFilteredRows.filter(rowHasDataForSelectedTypes);

  // 1) get latest start date (loads fast, but still show overlay so UX is consistent)
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        startLoading();
        const res = await Axios.get(`https://${Host}/latestdate/Inventory`, {
          params: queryParams,
          signal: ctrl.signal,
        });
        const iso = res.data?.[0]?.latest;
        if (iso) setPoStartdate(iso.slice(0, 10));
      } catch (error) {
        if (!Axios.isCancel(error)) console.error("latestdate/Inventory:", error);
      } finally {
        stopLoading();
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  // 2) main data load (show overlay immediately, pick final result set)
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        startLoading();

        const res = await Axios.get(`https://${Host}/realapi/Production/Temp_Inventory_All`, {
          params: queryParams,
          signal: ctrl.signal,
        });

        // Some MySQL drivers return multiple result sets when you use SET + CTE + SELECT
        // Safely choose the last array-like payload.
        const data = res?.data;
        console.log("Temp_Inventory fetch data:", data);
        let table = [];
        if (Array.isArray(data)) {
          const last = data[data.length - 1];
          if (Array.isArray(last)) table = last;
          else if (Array.isArray(data[2])) table = data[2]; // fallback to your old shape
        }

        table = (Array.isArray(table) ? table : []).filter(Boolean);
        setPostList(table);

        // If PO-only hides everything, flip to show all so the page isn't empty.
        if (table.length && showPOonly) {
          const hasAnyPO = table.some(r => typeof (r?.PO_Details || "") === "string" && (r.PO_Details || "").trim().length > 0);
          if (!hasAnyPO) setShowPOonly(false);
        }
      } catch (error) {
        if (!Axios.isCancel(error)) console.error("Temp_Inventory fetch:", error);
      } finally {
        stopLoading();
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Postartdate, Poenddate, customer, Partdesc]);

  // Print only the -R part table area
  function handlePrintR() {
    if (!printRAreaRef.current) return;
    const printContents = printRAreaRef.current.innerHTML;
    const win = window.open('', '', 'height=900,width=1200');
    win.document.write('<html><head><title>Print -R Required By Date</title>');
    win.document.write('<link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css"/>');
    win.document.write('</head><body>');
    win.document.write(printContents);
    win.document.write('</body></html>');
    win.document.close();
    win.focus();
  }
  function handlePrintF() {
    if (!printFAreaRef.current) return;
    const printContents = printFAreaRef.current.innerHTML;
    const win = window.open('', '', 'height=900,width=1200');
    win.document.write('<html><head><title>Print -F Required By Date</title>');
    win.document.write('<link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css"/>');
    win.document.write('</head><body>');
    win.document.write(printContents);
    win.document.write('</body></html>');
    win.document.close();
    win.focus();
  }

  return (
    <div className="MainPage_data" style={{ position: 'relative' }}>
      {/* ===== Loading Overlay ===== */}
      {loading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 2000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(2px)'
        }}>
          <div className="card p-4 text-center" style={{ minWidth: 260 }}>
            <div className="spinner-border" role="status" aria-hidden="true" />
            <div className="mt-3 fw-bold">Loading inventory…</div>
            <div className="text-muted">Elapsed: {elapsed}s</div>
          </div>
        </div>
      )}

      <div>
        <br />
        <Link to="/">
          <input type="button" className="btn btn-success" defaultValue="Logout" id="logout" />
        </Link>
      </div>

      <div className="Container">
        <h1>Rigid/Flex Inventory</h1>
        <div className="row mb-3">
          <div className="col-auto">
            <button
              className="btn btn-outline-primary mb-2"
              onClick={() => setShowPOonly(prev => !prev)}
            >
              {showPOonly ? 'Show ALL parts' : 'Show parts WITH PO only'}
            </button>
          </div>

          {/* Part Type Filter Checkboxes */}
          <div className="col-auto">
            <div className="d-flex align-items-center border p-2 rounded">
              <div className="mr-3">
                <strong>Filter by Part Type : </strong>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="flexCheckbox"
                  checked={flexChecked}
                  onChange={() => setFlexChecked(!flexChecked)}
                />
                <label className="form-check-label" htmlFor="flexCheckbox">
                  Flex (-F)
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="rigidCheckbox"
                  checked={rigidChecked}
                  onChange={() => setRigidChecked(!rigidChecked)}
                />
                <label className="form-check-label" htmlFor="rigidCheckbox">
                  Rigid (-R)
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="woodenCheckbox"
                  checked={woodenChecked}
                  onChange={() => setWoodenChecked(!woodenChecked)}
                />
                <label className="form-check-label" htmlFor="woodenCheckbox">
                  Wooden (-W)
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="metalCheckbox"
                  checked={metalChecked}
                  onChange={() => setMetalChecked(!metalChecked)}
                />
                <label className="form-check-label" htmlFor="metalCheckbox">
                  Metal (-M)
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="slapfoamCheckbox"
                  checked={slapfoamChecked}
                  onChange={() => setSlapfoamChecked(!slapfoamChecked)}
                />
                <label className="form-check-label" htmlFor="slapfoamCheckbox">
                  Slap Foam (-S)
                </label>
              </div>
            </div>
          </div>

          <div className="col">
            <input type='text' className="form-control form-rounded" placeholder='Customer' list="Customers" onChange={(e) => setCustomer(e.target.value)} />
            <datalist id="Customers">
              <option></option>
              {[...new Set(postList.map(x => x.Customer))].map(customer => (
                <option key={customer}>{customer}</option>
              ))}
            </datalist>
          </div>

          <div className="col">
            <div className="row">
              <div className="col" id='moldng'>
                <input type='date' className="form-control form-rounded" placeholder='PO SHIP DATE' defaultValue={Postartdate} onChange={(e) => setPoStartdate(e.target.value)} />
              </div>
              <div className="col" id='moldng'>
                <input type='date' className="form-control form-rounded" placeholder='PO SHIP DATE' defaultValue={Poenddate} onChange={(e) => setPoenddate(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="col">
            <div className="row">
              <input type='text' className="form-control form-rounded" placeholder='Part Description' list='Part_Desc' onChange={(e) => setPartdesc(e.target.value)} />
              <datalist id="Part_Desc">
                <option></option>
                {postList.map(x => (
                  <option key={x.Part_description}>{x.Part_description}</option>
                ))}
              </datalist>
            </div>
          </div>

          <div className="col-auto">
            <div className="row">
              <Link to="/Tentative_schedule">
                <button className="btn btn-primary">Tentative Schedule</button>
              </Link>
            </div>
          </div>
        </div>

        {/* Show filtered row count */}
        <div className="alert alert-info mb-3">
          Showing <strong>{filteredRows.length}</strong> of <strong>{poFilteredRows.length}</strong> rows
          (Filtering to show only : {flexChecked ? '-F ' : ''}{rigidChecked ? '-R ' : ''}{woodenChecked ? '-W' : ''}{metalChecked ? '-M ' : ''}{slapfoamChecked ? '-S' : ''})
          {!flexChecked && !rigidChecked && !woodenChecked && !metalChecked && !slapfoamChecked && ' (No part types selected - showing nothing)'} &nbsp;
          <strong>Main Part : </strong>Flex Connected &nbsp;<strong>Sub Part : </strong>Shipping Connected        </div>

        <br />

        <div className="scrollable-table" style={{ overflowX: 'auto' }}>
          <table className="table table-bordered table-striped table-sm" style={{ maxWidth: '100%' }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>PART NO</th>
                <th style={{ maxWidth: '120px' }}>PART DESCRIPTION</th>
                <th style={{ whiteSpace: 'nowrap' }}>CUSTOMER</th>
                <th style={{ maxWidth: '110px' }}>PO DETAILS</th>
                <th style={{ maxWidth: '110px' }}>INVENTORY QTY</th>
                <th style={{ maxWidth: '50px' }}>PRODUCED PARTS</th>
                <th style={{ whiteSpace: 'nowrap' }}>SHIPPED</th>
                <th style={{ maxWidth: '110px' }}>SCRAP PARTS</th>
                <th style={{ maxWidth: '110px' }}>ON HAND PARTS</th>
                <th style={{ maxWidth: '150px' }}>PO QTY</th>
                <th style={{ maxWidth: '150px' }}>REQUIRED PARTS</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, key) => {
                // Apply strict filtering to each cell - show ONLY lines that match selected types
                const filteredPO_Details = (row.PO_Details);
                const filteredInventoryList = filterListBySelectedTypes(row.Inventory_QTY_List);
                const filteredProducedList = filterListBySelectedTypes(row.Produced_Parts_List);
                const filteredScrapList = filterListBySelectedTypes(row.Scrap_Parts_List);
                const filteredPendingList = filterListBySelectedTypes(row.Pending_Parts_List);
                const filteredPOList = filterListBySelectedTypes(row.PO_QTY_List);
                const filteredRequiredList = filterListBySelectedTypes(row.Required_Ship_QTY_List);

                // Check if filtered ON HAND PARTS contains negative values
                const hasNegativeOnHand = filteredPendingList &&
                  /\(-\d+(\.\d+)?\)/.test(filteredPendingList);

                return (
                  <tr
                    key={key}
                    className={hasNegativeOnHand ? 'negative-row' : ''}
                  >
                    <td style={{ whiteSpace: 'nowrap' }}>{row.PartNo}</td>
                    <td>{row.Part_description}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{row.Customer}</td>
                    <td style={{ verticalAlign: 'top' }}><BorderedLinesCell text={row.PO_Details} /></td>
                    <td style={{ verticalAlign: 'top' }}><BorderedLinesCell text={filteredInventoryList} /></td>
                    <td style={{ verticalAlign: 'top' }}><BorderedLinesCell text={filteredProducedList} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{row.Shipped_Parts}</td>
                    <td style={{ verticalAlign: 'top' }}><BorderedLinesCell text={filteredScrapList} /></td>
                    <td style={{ verticalAlign: 'top' }}><BorderedLinesCell text={filteredPendingList} /></td>
                    <td style={{ verticalAlign: 'top' }}><BorderedLinesCell text={filteredPOList} /></td>
                    <td style={{ verticalAlign: 'top' }}><BorderedLinesCell text={filteredRequiredList} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className='row' style={{ 'flexWrap': 'nowrap' }}>
          <div className='col-6'>
            <RigidSchedulePage postList={postList} />
          </div>
          <div className='col-6'>
            <FlexSchedulePage postList={postList} />
          </div>
        </div>
        <br /> <br />
        <div className='row' style={{ 'flexWrap': 'nowrap' }}>
          <div className='col-6'>
            {/*<Insert_Metal_Req postList={postList} />*/}
            <Slap_foam_Schedule_Page postList={postList} />
          </div>
          <div className='col-6'>
            <Insert_Wood_Req postList={postList} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Temp_Inventory;